/**
 * What a CSG root's boolean is made of, pure and React-free. A tree, since Godot folds each subtree
 * before its parent combines it by the child's operation (`csg_shape.cpp:472,481`). Only CSG children
 * count, as `parent_shape` is set for a direct CSG parent: `CSGBox3D > Node3D > CSGSphere3D` is two roots.
 */
import * as THREE from 'three';
import { joinPath } from '../../utils/nodePath';
import { localMatrix3D } from '../nodeTreeTransforms';
import { patchedNode } from '../nodePatch';
import { CsgOperation } from '../../godot/scene/csg.js';
function isVisible(node, path, hidden) {
    if (node.properties.visible === false)
        return false;
    return !hidden?.has(path);
}
/**
 * The CSG children of `node`, with their paths, each as the live scene passes bind it. Descending
 * ONLY into CSG-typed children is the rule the header explains, so both walks below read it from here.
 */
function csgChildren(node, path, { lookup, patches }) {
    const out = [];
    for (const child of node.children) {
        if (lookup(child.type) === null)
            continue;
        const childPath = joinPath(path, child.name);
        out.push([patchedNode(child, patches?.get(childPath)), childPath]);
    }
    return out;
}
/** NaN or Infinity anywhere in a matrix would propagate into the BVH builder. */
function isFinite4(m) {
    return m.elements.every((n) => Number.isFinite(n));
}
/**
 * Build the plan for the CSG root at `rootPath`.
 *
 * Returns null when the node is not a CSG shape at all. A root with no usable
 * contributions still returns a plan, with an empty `contributions` list: "this root
 * legitimately draws nothing" is a different answer from "this is not a CSG root".
 */
export function buildCsgPlan(root, rootPath, options) {
    const { lookup, hiddenPaths } = options;
    if (lookup(root.type) === null)
        return null;
    const absorbedPaths = new Set();
    const invisiblePaths = new Set();
    /** The skipped node and every CSG node under it, where the recursion stopped. */
    const markInvisible = (node, path) => {
        invisiblePaths.add(path);
        for (const [child, childPath] of csgChildren(node, path, options))
            markInvisible(child, childPath);
    };
    const keyParts = [`root:${root.type}`];
    let geometryCount = 0;
    const visit = (node, path, parentMatrix, isRoot) => {
        // A root builds whatever its visibility, since update_shape() is gated on is_root_shape()
        // alone (csg_shape.cpp:568-570), so only a child stops the walk.
        if (!isRoot && !isVisible(node, path, hiddenPaths)) {
            markInvisible(node, path);
            return null;
        }
        // The root's own transform is NOT baked in: the result mesh is mounted inside the
        // root's own transform group, so including it here would apply it twice.
        const matrix = isRoot ? new THREE.Matrix4() : parentMatrix.clone().multiply(localMatrix3D(node));
        if (!isRoot)
            absorbedPaths.add(path);
        const props = node.properties;
        // A root's operation is inert; Godot has nothing to fold it into.
        const operation = isRoot
            ? CsgOperation.UNION
            : typeof props.operation === 'number'
                ? props.operation
                : CsgOperation.UNION;
        const shape = lookup(node.type);
        let hasGeometry = shape?.hasGeometry === true;
        if (hasGeometry && !isFinite4(matrix)) {
            keyParts.push(`${path}:nonfinite`);
            hasGeometry = false;
        }
        else if (hasGeometry) {
            const materialPath = typeof props.materialPath === 'string' ? props.materialPath : undefined;
            geometryCount++;
            keyParts.push(`${path}|${node.type}|${operation}|${shape.key(node)}|${materialPath ?? ''}|` +
                matrix.elements.map((n) => n.toFixed(6)).join(','));
        }
        else {
            // A combiner draws nothing, but its operation decides how its fold lands.
            keyParts.push(`${path}|${node.type}|${operation}|fold`);
        }
        const children = [];
        for (const [child, childPath] of csgChildren(node, path, options)) {
            const folded = visit(child, childPath, matrix, false);
            if (folded)
                children.push(folded);
        }
        if (!hasGeometry && children.length === 0)
            return null;
        return { path, type: node.type, operation, matrix, node, hasGeometry, children };
    };
    const tree = visit(root, rootPath, new THREE.Matrix4(), true);
    return {
        rootPath,
        root: tree,
        geometryCount,
        absorbedPaths,
        invisiblePaths,
        cacheKey: keyParts.join('\n'),
    };
}
//# sourceMappingURL=csgPlan.js.map